import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { api } from "@/lib/api";
import { fmtOdds, SPORT_META } from "@/lib/odds";

const R = { won: "text-emerald-400", lost: "text-red-400", pending: "text-slate-500" };
const Res = ({ r }) => <span data-testid="history-result" className={`text-[10px] font-black uppercase ${R[r] || R.pending}`}>{r === "pending" ? "—" : r}</span>;

export function FavoritesHistory() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/favorites/history?days=7").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || !d.days.length) return null;
  const rec = d.record;
  const fmt = (k) => `${rec[k].won}–${rec[k].lost}`;

  return (
    <section className="mt-12" data-testid="favorites-history">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><History size={20} className="text-slate-400" /> How the favorites finished</h2>
        <div className="flex flex-wrap gap-4 text-xs text-slate-400" data-testid="history-record">
          <span>Lock <b className="font-mono-num text-white">{fmt("lock")}</b></span>
          <span>Parlay of the Day <b className="font-mono-num text-white">{fmt("potd")}</b></span>
          {rec.horse_win.won + rec.horse_win.lost > 0 && <span>Horse wins <b className="font-mono-num text-white">{fmt("horse_win")}</b></span>}
          {rec.exacta.won + rec.exacta.lost > 0 && <span>Exactas <b className="font-mono-num text-white">{fmt("exacta")}</b></span>}
          {rec.trifecta && rec.trifecta.won + rec.trifecta.lost > 0 && <span>Trifectas <b className="font-mono-num text-white">{fmt("trifecta")}</b></span>}
          {rec.sport_parlays.won + rec.sport_parlays.lost > 0 && <span>Sport parlays <b className="font-mono-num text-white">{fmt("sport_parlays")}</b></span>}
          {rec.safe && rec.safe.won + rec.safe.lost > 0 && <span>Safe parlays <b className="font-mono-num text-white">{fmt("safe")}</b></span>}
          {rec.lottery && rec.lottery.won + rec.lottery.lost > 0 && <span>Lottery tickets <b className="font-mono-num text-white">{fmt("lottery")}</b></span>}
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {d.days.map((day, i) => (
          <div key={day.date} data-testid="history-day" className={`glass-card fade-up grid gap-4 p-5 md:grid-cols-[110px_1fr_1fr_1fr]`} style={{ animationDelay: `${i * 40}ms` }}>
            <div><p className="label-caps">{i === 0 ? "Yesterday" : "Slate"}</p><p className="font-display text-2xl font-bold text-white">{new Date(day.date + "T12:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p></div>
            <div>
              <p className="label-caps">Lock</p>
              <p className="mt-1 flex items-center gap-2 text-sm"><span className="h-4 w-1 rounded-full" style={{ background: SPORT_META[day.lock.sport]?.color }} /><span className="truncate font-semibold text-white">{day.lock.selection}</span><span className="font-mono-num text-amber-400">{fmtOdds(day.lock.odds)}</span><Res r={day.lock.result} /></p>
              <p className="truncate text-xs text-slate-500">{day.lock.matchup}</p>
            </div>
            <div>
              <p className="label-caps">Parlay of the day</p>
              {day.parlay_of_the_day ? (
                <>
                  <p className="mt-1 flex items-center gap-2 text-sm"><span className="truncate font-semibold text-white">{day.parlay_of_the_day.title}</span><span className="font-mono-num text-amber-400">{fmtOdds(day.parlay_of_the_day.combined_american)}</span><Res r={day.parlay_of_the_day.status} /></p>
                  <p className="text-xs text-slate-500">{day.parlay_of_the_day.legs.map((l) => (l.result === "won" ? "✓" : l.result === "lost" ? "✕" : "·")).join(" ")} {day.parlay_of_the_day.legs.filter((l) => l.result === "won").length}/{day.parlay_of_the_day.legs.length} legs hit</p>
                </>
              ) : <p className="text-xs text-slate-500">—</p>}
            </div>
            <div>
              <p className="label-caps">{day.horse_picks.length ? "Horse picks" : "Sport parlays"}</p>
              {!day.horse_picks.length && <p className="mt-1 text-xs text-slate-400">{day.sport_parlays?.length ? `${day.sport_parlays.filter((s) => s.status === "won").length}/${day.sport_parlays.filter((s) => s.status !== "pending").length} league builds cashed` : "—"}</p>}
              <ul className="mt-1 space-y-0.5 text-xs">
                {day.horse_picks.map((h) => (
                  <li key={h.matchup} className="flex items-center gap-2 truncate">
                    <span className="truncate text-slate-400">{h.matchup.split(" · ")[0]}</span>
                    {h.win_pick && <span className="truncate text-white">{h.win_pick.selection.replace(" to Win", "")}</span>}{h.win_pick && <Res r={h.win_pick.result} />}
                    {h.exacta && <span className="text-slate-500">· EX</span>}{h.exacta && <Res r={h.exacta.result} />}
                    {h.trifecta && <span className="text-slate-500">· TRI</span>}{h.trifecta && <Res r={h.trifecta.result} />}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
